import BoldSmallText from '@/components/BoldSmallText';
import RegularText from '@/components/RegularText';
import Switch from '@/components/Switch';
import { BOOLEAN_STRING, BORDER_RADIUS_OPTIONS, CAPTION_LAYOUT_OPTIONS, DAY_OF_WEEK_OPTIONS } from '@/constants/enum';
import { handleSaveWidgetSettings, widgetSettingSelector } from '@/redux/slice/widget.slice';
import { BlockStack, FormLayout, InlineStack, Select } from '@shopify/polaris';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';

export const WidgetCalendar = () => {
  const { t } = useTranslation('widget_settings');
  const widgetSettings = useSelector(widgetSettingSelector);
  const dispatch = useDispatch();

  const borderRadiusOptions = useMemo(
    () =>
      Object.values(BORDER_RADIUS_OPTIONS).map((option) => ({
        label: t(`sizes.borderRadius.options.${option}`),
        value: option,
      })),
    [t],
  );

  const captionsLayoutOptions = useMemo(
    () =>
      Object.values(CAPTION_LAYOUT_OPTIONS).map((option) => ({
        label: t(`customization.captionLayout.options.${option}`),
        value: option,
      })),
    [t],
  );

  const weekStartsOnOptions = useMemo(
    () =>
      Object.values(DAY_OF_WEEK_OPTIONS)
        .filter((item) => item === DAY_OF_WEEK_OPTIONS.MONDAY || item === DAY_OF_WEEK_OPTIONS.SUNDAY)
        .map((option) => ({
          label: t(`customization.weekStartsOn.options.${option}`),
          value: option,
        })),
    [t],
  );

  const showOutsideDaysOptions = useMemo(() => {
    return [
      {
        label: 'Yes',
        value: BOOLEAN_STRING.TRUE,
      },
      {
        label: 'No',
        value: BOOLEAN_STRING.FALSE,
      },
    ];
  }, []);

  return (
    <BlockStack gap={'300'}>
      <BoldSmallText>{t('widgetDesign.general.calendar.title')}</BoldSmallText>
      <InlineStack align="space-between">
        <RegularText>{t('widgetDesign.general.calendar.showCalendar')}</RegularText>
        <Switch
          isActive={widgetSettings.customizations.alwaysShowCalendar}
          onSwitch={() => {
            dispatch(
              handleSaveWidgetSettings({
                customizations: {
                  ...widgetSettings.customizations,
                  alwaysShowCalendar: !widgetSettings.customizations.alwaysShowCalendar,
                },
              }),
            );
          }}
        />
      </InlineStack>
      <FormLayout.Group>
        <Select
          label={t('customization.weekStartsOn.label')}
          options={weekStartsOnOptions}
          value={widgetSettings.customizations.weekStartsOn}
          onChange={(value: DAY_OF_WEEK_OPTIONS) => {
            dispatch(
              handleSaveWidgetSettings({
                customizations: {
                  ...widgetSettings.customizations,
                  weekStartsOn: value,
                },
              }),
            );
          }}
        />
        <Select
          label={t('customization.captionLayout.label')}
          options={captionsLayoutOptions}
          value={widgetSettings.customizations.captionLayout}
          onChange={(value: CAPTION_LAYOUT_OPTIONS) => {
            dispatch(
              handleSaveWidgetSettings({
                customizations: {
                  ...widgetSettings.customizations,
                  captionLayout: value,
                },
              }),
            );
          }}
        />
        <Select
          label={t('sizes.borderRadius.label')}
          options={borderRadiusOptions}
          value={widgetSettings.sizes.borderRadius}
          onChange={(value: BORDER_RADIUS_OPTIONS) => {
            dispatch(
              handleSaveWidgetSettings({
                sizes: {
                  ...widgetSettings.sizes,
                  borderRadius: value,
                },
              }),
            );
          }}
        />
        <Select
          label={t('customization.showOutsideDays.label')}
          options={showOutsideDaysOptions}
          value={widgetSettings.customizations.showOutsideDays ? BOOLEAN_STRING.TRUE : BOOLEAN_STRING.FALSE}
          onChange={(checked) => {
            dispatch(
              handleSaveWidgetSettings({
                customizations: {
                  ...widgetSettings.customizations,
                  showOutsideDays: !!Number(checked),
                },
              }),
            );
          }}
        />
      </FormLayout.Group>
    </BlockStack>
  );
};
